<template>
  <div class="CourtCarouselSelect">
    <div
      ref="trackRef"
      class="CourtCarouselSelect__track"
    >
      <div class="CourtCarouselSelect__buttonContainer">
        <FormButton
          class="CourtCarouselSelect__button"
          iconName="locationPlus"
          :orientation="courts.length > 0 ? 'vertical' : 'horizontal'"
          @click="openModal({ name: RouteName.courtNew })"
        >
          <span v-if="courts.length === 0">Add your court</span>
          <span v-else>Add a court</span>
        </FormButton>
        <FormButton
          v-if="courts.length > 0"
          class="CourtCarouselSelect__button"
          iconName="locationMap"
          :orientation="courts.length > 0 ? 'vertical' : 'horizontal'"
          @click="openModal({ name: RouteName.courtsManage })"
        >
          Manage courts
        </FormButton>
      </div>
      <label
        v-for="court in courts"
        :key="court.id"
        ref="itemRefs"
        :id="`label-${court.id}`"
        class="CourtCarouselSelect__item"
        data-testid="courtCarouselItemlabel"
        :aria-selected="court.id === selectedCourtId"
        :data-court-id="court.id"
        @click.prevent="handleCourtClick"
      >
        <input
          v-model="selectedCourtId"
          type="radio"
          class="CourtCarouselSelect__itemInput"
          :value="court.id"
          :aria-labelledby="`label-${court.id}`"
          required
        >
        <CourtImage
          class="CourtCarouselSelect__itemImage"
          :modelValue="court"
        />
        <div class="CourtCarouselSelect__itemLabel">
          <strong class="CourtCarouselSelect__itemName">{{ court.name }}</strong>
          <span class="CourtCarouselSelect__itemAddress">
            {{ court.address ?? "Unknown location" }}
          </span>
        </div>
      </label>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { RouteName } from "@/lib/router/routes.ts";
import haptic from "@/lib/utils/platform/haptic";
import useModalStore from "@/stores/useModalStore.ts";
import { Court } from "@shared/lib/database/query.types";
import { nextTick, onBeforeUnmount, useTemplateRef, watch } from "vue";
import CourtImage from "./CourtImage.vue";
import FormButton from "./FormButton.vue";

const selectedCourtId = defineModel<string | null>();

const {
  courts,
} = defineProps<{
  courts: Array<Court>,
}>();

const {
  openModal,
} = useModalStore();

const itemRefs = useTemplateRef<Array<HTMLElement>>("itemRefs");
const trackRef = useTemplateRef<HTMLElement>("trackRef");

const intersectionObserver = new IntersectionObserver(handleIntersection, {
  threshold: 0.5,
});

onBeforeUnmount(() => {
  intersectionObserver.disconnect();
});

watch(() => courts, async (newCourts, oldCourts) => {
  await nextTick();
  handleItemRefsChange();

  if (oldCourts && newCourts.length === oldCourts.length + 1) {
    const newCourt = newCourts.find((court) => !oldCourts.some((oldCourt) => oldCourt.id === court.id));

    if (newCourt) scrollToCourt(newCourt.id);
  } else if (selectedCourtId.value) {
    scrollToCourt(selectedCourtId.value);
  }
}, {
  immediate: true,
});

watch(selectedCourtId, () => {
  haptic();
});

function scrollToCourt (courtId: string) {
  const el = itemRefs.value?.find((itemRef) => itemRef.dataset.courtId === courtId);

  el?.parentElement?.scrollTo({
    behavior: "instant",
    left: el?.offsetLeft,
  });
}

function handleItemRefsChange () {
  intersectionObserver.disconnect();

  if (!itemRefs.value) return;

  itemRefs.value.forEach((itemRef) => {
    intersectionObserver.observe(itemRef);
  });
}

function handleIntersection (entries: Array<IntersectionObserverEntry>) {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      selectedCourtId.value = (entry.target as HTMLElement).dataset.courtId || null;
    }
  });
}

function handleCourtClick (e: MouseEvent) {
  if (!trackRef.value) return;

  const el = e.currentTarget as HTMLElement;

  trackRef.value.scrollTo({
    behavior: "smooth",
    left: el.offsetLeft,
    top: 0,
  });
}
</script>

<style lang="scss" scoped>
.CourtCarouselSelect {
  margin-inline: -16px;
}

.CourtCarouselSelect__track {
  display: flex;
  grid-auto-flow: column;
  gap: 16px;
  padding-inline: 16px;
  overflow-x: auto;

  scroll-padding-inline: 16px;

  scrollbar-width: none;
  scroll-snap-type: x mandatory;

  &::-webkit-scrollbar {
    display: none;
  }

  &:has(:disabled) {
    overflow-x: hidden;
  }

  &:has(> .CourtCarouselSelect__item:only-child) {
    grid-auto-columns: 100%;
  }
}

.CourtCarouselSelect__buttonContainer {
  display: grid;
  gap: 12px;

  &:only-child {
    flex: 1 1 auto;
  }

  &:not(:only-child) {
    flex: 0 0 0;
    scroll-snap-align: start;
  }
}

.CourtCarouselSelect__buttonLabel {
  font-weight: var(--fontWeightMedium);
  font-stretch: var(--fontWidthExtended);
  line-height: 1.1;
}

.CourtCarouselSelect__item {
  position: relative;
  isolation: isolate;

  display: grid;
  flex: 1 1 auto;
  grid: auto min-content / auto;
  min-width: calc(100% - 24px);
  overflow: hidden;

  border: solid 2px var(--themeColorNeutral900);
  border-radius: 8px;
  background-color: var(--themeColorWhite);

  scroll-snap-align: center;
  transition: 350ms;
  transition-property: opacity, border-color;

  &[aria-selected="false"] {
    opacity: 0.5;
  }

  &:has(:disabled) {
    border-color: var(--themeColorNeutral400);
  }
}

.CourtCarouselSelect__itemInput {
  position: absolute;
  top: 0;
  left: 0;

  opacity: 0;

  pointer-events: none;
}

.CourtCarouselSelect__itemImage {
  max-height: 240px;
  transition: 350ms filter;

  .CourtCarouselSelect__itemInput:disabled + & {
    filter: grayscale(100%) opacity(0.6);
  }
}

.CourtCarouselSelect__itemLabel {
  position: relative;
  isolation: isolate;

  display: grid;
  gap: 8px;
  padding: calc(12px + var(--fontSafariOffset)) 12px 12px;

  border-top: solid 2px var(--themeColorNeutral900);
  background-color: var(--themeColorSecondary);
  color: var(--themeColorSecondaryForeground);

  font-size: 16px;
  font-weight: var(--fontWeightMedium);
  font-stretch: var(--fontWidthExtended);

  transition: 350ms background-color;
  transition-property: background-color, color, border-color;

  .CourtCarouselSelect__itemInput:disabled ~ & {
    border-color: var(--themeColorNeutral400);
    background-color: var(--themeColorNeutral100);
    color: var(--themeColorNeutral500);
  }
}

.CourtCarouselSelect__itemName {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.CourtCarouselSelect__itemAddress {
  overflow: hidden;

  color: color-mix(in srgb, var(--themeColorSecondaryForeground) 60%, transparent);

  font-size: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;

  .CourtCarouselSelect__itemInput:disabled ~ .CourtCarouselSelect__itemLabel & {
    color: var(--themeColorNeutral500);
  }
}
</style>
